import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Radar as RadarIcon, Flame, Gem, Crown, ArrowUp, ArrowDown, ExternalLink, Info } from 'lucide-react';
import { Card, CardTitle, CardDescription, Skeleton } from '@/components/ui';
import { ScoreBadge } from '@/components/keyword';
import { api } from '@/lib/api';
import { formatNumber, type RadarItem } from '@keywordgod/shared';
import { cn } from '@/lib/utils';

type Tab = 'burst' | 'evergreen' | 'golden';

/**
 * Phase 60: 키워드 레이더 — 전 분야 통합 발굴
 * 🚀 오늘 터질 / 💎 평생 우물 / 👑 황금 타이밍
 */
export function Radar() {
  const [tab, setTab] = useState<Tab>('burst');
  const q = useQuery({
    queryKey: ['radar'],
    queryFn: () => api.radar.get(),
    staleTime: 30 * 60 * 1000,
    // WARMING(준비 중) 이면 자동 재시도 — 백그라운드 워밍 완료까지 폴링
    retry: (count, err) => {
      const code = (err as Error & { code?: string }).code;
      return code === 'WARMING' && count < 12;
    },
    retryDelay: 6000,
  });
  const warming = (q.error as (Error & { code?: string }) | null)?.code === 'WARMING' || (q.isError && (q.error as Error & { code?: string })?.code === 'WARMING');

  const tabs: Array<{ id: Tab; label: string; icon: typeof Flame; tone: string; desc: string }> = [
    { id: 'burst', label: '오늘 터질', icon: Flame, tone: 'orange', desc: '지금 글 쓰면 조회수 폭발 — 급상승 중인 키워드' },
    { id: 'evergreen', label: '평생 우물', icon: Gem, tone: 'emerald', desc: '한 번 쓰면 꾸준히 조회수를 빨아먹는 안정 키워드' },
    { id: 'golden', label: '황금 타이밍', icon: Crown, tone: 'gold', desc: '지금도 뜨고 + 꾸준함까지 — 최상의 키워드' },
  ];
  const list: RadarItem[] = q.data ? q.data[tab] : [];
  const activeTab = tabs.find((t) => t.id === tab)!;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <RadarIcon className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">키워드 레이더</span>
          <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">
            전 분야
          </span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          쇼핑·정책·건강·연예 가리지 않고, 전 분야에서 <strong>오늘 터질 / 평생 갈</strong> 키워드를 매시간 골라드립니다.
        </p>
      </header>

      <Card className="border-gold/20 bg-gradient-to-r from-gold/5 to-transparent">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 shrink-0 text-gold mt-0.5" />
          <p className="text-xs text-gray-300 leading-relaxed">
            DataLab 60일 검색 추이로 <strong className="text-orange-300">폭발력</strong>(최근 급상승)과{' '}
            <strong className="text-emerald-300">지속력</strong>(변동 적고 꾸준)을 계산. 블로거가 "오늘 뭘 쓸지" /
            "묻어둘 글은 뭘 쓸지" 한눈에 정합니다. 매시간 자동 갱신.
          </p>
        </div>
      </Card>

      {/* 탭 */}
      <div className="flex items-center gap-2 border-b border-gray-800">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
              tab === t.id
                ? t.tone === 'orange'
                  ? 'border-orange-400 text-orange-300'
                  : t.tone === 'emerald'
                  ? 'border-emerald-400 text-emerald-300'
                  : 'border-gold text-gold'
                : 'border-transparent text-gray-400 hover:text-white',
            )}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            {q.data && (
              <span className="ml-1 rounded-full bg-gray-800 px-1.5 py-0.5 text-[10px] text-gray-400">
                {q.data[t.id].length}
              </span>
            )}
          </button>
        ))}
      </div>

      <p className="text-sm text-gray-400">{activeTab.desc}</p>

      {(q.isLoading || warming) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
          <p className="col-span-full text-xs text-gray-500">
            {warming
              ? '레이더 준비 중… 전 분야 키워드의 60일 추이를 계산하고 있어요 (약 1분, 자동 새로고침)'
              : '불러오는 중…'}
          </p>
        </div>
      )}

      {q.isError && !warming && (
        <Card className="border-red-500/40 bg-red-500/10 text-red-200 text-sm">
          레이더 조회 실패: {(q.error as Error).message}
        </Card>
      )}

      {q.data && list.length === 0 && (
        <Card className="text-gray-400 text-center py-8">
          이 분류에 해당하는 키워드가 아직 없습니다. 다음 갱신(매시간)을 기다려주세요.
        </Card>
      )}

      {list.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((it, i) => (
            <RadarCard key={it.keyword} item={it} rank={i + 1} />
          ))}
        </div>
      )}

      {q.data && (
        <p className="text-xs text-gray-600 text-center">
          후보 {q.data.totalScanned}개 스캔 · 갱신 {new Date(q.data.generatedAt * 1000).toLocaleString('ko-KR')}
        </p>
      )}
    </div>
  );
}

function RadarCard({ item, rank }: { item: RadarItem; rank: number }) {
  return (
    <Link to={`/analyze/${encodeURIComponent(item.keyword)}`}>
      <Card className="h-full transition-transform hover:-translate-y-0.5">
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-bold text-gold">#{rank}</span>
          <ScoreBadge grade={item.grade} size="sm" />
        </div>
        <div className="mt-2 truncate text-base font-bold text-white flex items-center gap-1">
          {item.keyword}
          <ExternalLink className="h-3 w-3 text-gray-600 shrink-0" />
        </div>

        {/* 폭발력/지속력 미니 바 */}
        <div className="mt-3 space-y-1.5">
          <MiniBar label="🚀" score={item.burstScore} color="bg-orange-400" />
          <MiniBar label="💎" score={item.evergreenScore} color="bg-emerald-400" />
        </div>

        <div className="mt-2.5 flex items-center justify-between text-[11px] text-gray-500">
          <span className="tabular">월 {formatNumber(item.totalSearch)}</span>
          <span className="inline-flex items-center gap-0.5">
            {item.trendRate > 0.03 ? (
              <span className="text-emerald-300"><ArrowUp className="inline h-3 w-3" />{Math.round(item.trendRate * 100)}%</span>
            ) : item.trendRate < -0.03 ? (
              <span className="text-red-300"><ArrowDown className="inline h-3 w-3" />{Math.round(item.trendRate * 100)}%</span>
            ) : (
              <span>—</span>
            )}
          </span>
        </div>
      </Card>
    </Link>
  );
}

function MiniBar({ label, score, color }: { label: string; score: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs w-4">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-gray-900 overflow-hidden">
        <div className={cn('h-full', color)} style={{ width: `${score}%` }} />
      </div>
      <span className="tabular text-[11px] text-gray-400 w-6 text-right">{score}</span>
    </div>
  );
}
